import { includes, isNil } from 'lodash';
import { FunctionComponent, useCallback, useContext, useId, useMemo, useState } from 'react';
import { DeepPartial, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { Loading } from '../';
import { useCurrentUser } from '../../hooks';
import styled from '../../themes/themed-styled-components';
import { MutationResult, RoleName } from '../../types';
import { HacpackMutation, HacpackMutationQsaHandlingNotesArguments, HacpackQuery, HacpackQueryHandlingArguments, Handling, HandlingType } from '../../types/graph';
import { QueryLoaderContext } from '../QueryLoader';

const handlingQsaApprovalQuery = gql<HacpackQuery, HacpackQueryHandlingArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        id
        approvedByTerminal
        fei
        cei
        standardDistance
        qsaNotes
        terminalOpinionDate
        bookingCheckResult
        loadingUnitIdentifier
        type
        checkedIn
        terminal {
            id
            name
        }
    }
}`;

type HandlingRiskAssessmentProps = { handling: DeepPartial<Handling> };

const qsaHandlingNotesMutation = gql<{ qsaHandlingNotes: MutationResult }, HacpackMutation>`
    mutation ($handlingId: Long!, $qsaNotes: String!) {
        qsaHandlingNotes(handlingId: $handlingId, qsaNotes: $qsaNotes)  {
            isOk
        }
    }
`;

const QsaNotesText = styled.pre`
white-space: pre-wrap
`;

const HandlingQsaApproval: FunctionComponent<HandlingRiskAssessmentProps> = ({ handling: { id } }) => {

    const { t } = useTranslation('common');
    //const { terminal } = useContext(TerminalContext);
    const { reload } = useContext(QueryLoaderContext);

    const controlId = useId();

    const [result, executeQuery] = useQuery({
        query: handlingQsaApprovalQuery,
        variables: { id: id as number },
        pause: id === undefined,
        requestPolicy: 'cache-and-network'
    });

    const { data, fetching } = result;

    const handling = data?.handling;
    const terminal = handling?.terminal;

    const form = useForm<HacpackMutationQsaHandlingNotesArguments>({
        values: {
            handlingId: id ?? 0,
            qsaNotes: handling?.qsaNotes ?? ''
        },
        defaultValues: {
            //handlingId: id,
            //opinion: true,
            //qsaNotes: handling?.qsaNotes
        }
    });

    const { register, handleSubmit, formState } = form;

    const { errors } = formState;

    const [promptOpen, setPromptOpen] = useState(false);

    const togglePrompt = useCallback(() => setPromptOpen(promptOpen => !promptOpen), [setPromptOpen])
    const openPrompt = useCallback(() => setPromptOpen(true), [setPromptOpen])
    const closePrompt = useCallback(() => setPromptOpen(false), [setPromptOpen])

    const { isSuperuser, hasRoleForTerminal } = useCurrentUser();

    const [editResult, edit] = useMutation<{ approveHandlings: MutationResult }, HacpackMutationQsaHandlingNotesArguments>(qsaHandlingNotesMutation);

    const { fetching: fetchingEdit } = editResult;

    const canEdit = useMemo(() => {
        if (handling === null || terminal === null || handling === undefined || terminal === undefined) {
            return false;
        }
        const { type } = handling;

        if (type === HandlingType.Transit || type === HandlingType.None || terminal === null || terminal?.id === undefined) {
            return false;
        }

        return isSuperuser || hasRoleForTerminal(RoleName.QSA, terminal.id)
    }, [isSuperuser, hasRoleForTerminal, terminal, handling]);

    const onSubmit = useCallback<SubmitHandler<HacpackMutationQsaHandlingNotesArguments>>((values, e) => {
        const { qsaNotes, handlingId } = values;

        if (handlingId === undefined) {
            return;
        }

        edit({ handlingId, qsaNotes }).then(() => {
            reload();
            executeQuery();
            closePrompt();
            //window.location.reload();
        });
    }, [edit, id, reload, executeQuery, closePrompt])

    const onError = (errors: unknown, e: unknown) => console.debug({ errors, e });

    const prompt = useMemo(() => {
        if (handling === null || terminal === null || handling === undefined || terminal === undefined) {
            return null;
        }
        const { loadingUnitIdentifier } = handling;

        return <Modal isOpen={!fetchingEdit && promptOpen} toggle={togglePrompt}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <ModalHeader>{t(`Modifica note per unità ${loadingUnitIdentifier}`)}</ModalHeader>
                <ModalBody>
                    <Form tag="div">
                        <FormGroup>
                            <div className="d-none">
                                <input type="hidden" value={id} {...register('handlingId', { valueAsNumber: true })} />
                            </div>
                            <Label for={`qsaNotes_${controlId}`}>{t('Note QSA')}</Label>
                            <textarea className="form-control" {...register('qsaNotes')} id={`qsaNotes_${controlId}`} />
                            {errors.qsaNotes?.message !== undefined && <div className="invalid-feedback">{errors.qsaNotes.message}</div>}
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button type="submit" color="primary">{t('Conferma')}</Button>{' '}
                    <Button color="secondary" onClick={closePrompt}>{t('Annulla')}</Button>
                </ModalFooter>
            </form>
        </Modal>
    }, [t, id, handling, promptOpen, closePrompt, togglePrompt, fetching, register, handleSubmit, onSubmit, onError, errors, controlId])

    const editButton = useMemo(() => {
        if (handling === null || handling === undefined) {
            return null;
        }

        if (terminal === null || terminal === undefined || terminal.id === undefined || !includes(['TERMINAL DEL GOLFO'], terminal?.name)) {
            return null;
        }

        if (canEdit) {
            return <Button disabled={fetching} color='primary' onClick={openPrompt}>{t('Modifica')}</Button>
        }

        return null;

    }, [canEdit, t, handleSubmit, fetching, terminal, openPrompt, errors]);

    //if (qsaNotes === undefined || qsaNotes === null || qsaNotes === "") {
    //    return null;
    //}

    const description = useMemo(() => {
        if (handling === null || handling === undefined) {
            return null;
        }

        const { qsaNotes } = handling;

        if (isNil(qsaNotes) || qsaNotes.trim() === '') {
            return <p><i>Nessuna</i></p>
        }

        return <>
            {<QsaNotesText>{qsaNotes}</QsaNotesText>}
        </>;
    }, [t, handling]);

    if (fetching === true) {
        return <Loading />;
    }

    return <div>
        <div className="d-flex gap-2">
            <h4 className="m-0" style={editButton !== null ? { margin: 0 } : undefined}>{t('Note QSA')}</h4> {editButton}
        </div>
        {description}

        {prompt}
    </div>;

};

export default HandlingQsaApproval;